import type { Collection } from '@rstore/shared';
import type { MaybeRefOrGetter } from 'vue';
import type { GlobalCollectionId, GlobalDataSourceId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { ResolvedMonospaceCollection, ResolvedMonospaceDataSource, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import type { SchemaManifest } from '#layers/data-model/types/schema/schema-manifest';
import type { WorkspaceEntityStore } from '#layers/runtime/public';
import { normalizeCollectionRelations, resolveCollectionOppositeRelations } from '@rstore/core';
import { addCollections, removeCollection } from '@rstore/vue';
import { defineStore } from 'pinia';
import { computed, toValue } from 'vue';
import { getWorkspaceSchemaOverrides } from '#layers/data-model/rstore/overrides/apply-overrides';
import { loadWorkspaceSchema } from '#layers/data-model/utils/schema/load-workspace-schema';
import { QuerySchemaWalker } from '#layers/data-model/utils/schema/query-schema';
import { toStoreCollection } from '#layers/data-model/utils/schema/to-store-collection';
import { defineWorkspaceStateHydration, workspaceRuntime } from '#layers/runtime/public';

interface RegisterWorkspaceCollectionsOptions {
	sources: ResolvedMonospaceDataSource[];
	store: WorkspaceEntityStore;
}

/**
 * The workspace's schema manifest. Loading it writes the schema rows into the runtime's cache; once
 * loaded, one rstore collection per workspace collection is registered on that runtime's store.
 */
export const workspaceSchemaHydration = defineWorkspaceStateHydration(
	(runtime) => loadWorkspaceSchema({ store: runtime.store, workspaceFetch: runtime.fetch }),
	{
		empty: null,
		onLoaded: (runtime, manifest) => registerWorkspaceCollections({ sources: manifestDataSources(runtime.store, manifest), store: runtime.store }),
	},
);

/** The manifest's data sources, read from the normalized rows loading wrote into `store`. */
function manifestDataSources(store: WorkspaceEntityStore, manifest: SchemaManifest): ResolvedMonospaceDataSource[] {
	const ids = new Set(manifest.monospaceSchema.sources.map((source) => source.id));
	return store.MonospaceDataSource.peekMany().filter((source) => ids.has(source.id));
}

/** Registers the loaded schema on its store: schema overrides, then one rstore collection per workspace collection. */
function registerWorkspaceCollections({ sources, store }: RegisterWorkspaceCollectionsOptions) {
	const collections = sources.flatMap((source) => source.collections);
	getWorkspaceSchemaOverrides(store).apply(sources);
	cleanupWorkspaceSchema({ currentCollections: collections, store });
	const collectionsByName = new Map(store.$collections.map((collection) => [collection.name, collection]));

	const newCollections: Collection[] = [];
	let patchedRelations = false;

	for (const monospaceCollection of collections) {
		const storeCollection = toStoreCollection(monospaceCollection);
		const existingCollection = collectionsByName.get(monospaceCollection.apiName);

		if (existingCollection) {
			existingCollection.meta ??= {};

			if (!monospaceCollection.isSystem) {
				const { getKey } = storeCollection;

				existingCollection.fields = storeCollection.fields;
				Object.assign(existingCollection.meta, storeCollection.meta);
				existingCollection.relations = storeCollection.relations;
				normalizeCollectionRelations([existingCollection]);
				existingCollection.scopeId = storeCollection.scopeId;

				if (getKey) {
					existingCollection.getKey = (item: AnyItem) => item.$overrideKey ?? getKey(item);
				}

				patchedRelations = true;
			}
			else {
				Object.assign(existingCollection.meta, {
					relationsWithConstraint: storeCollection.meta.relationsWithConstraint,
				});
			}

			continue;
		}

		newCollections.push(storeCollection);
	}

	addCollections(store, newCollections);

	if (patchedRelations && newCollections.length === 0) resolveCollectionOppositeRelations(store.$collections);
}

function cleanupWorkspaceSchema({
	currentCollections,
	store,
}: { currentCollections: ResolvedMonospaceCollection[]; store: WorkspaceEntityStore }) {
	const collectionNames = new Set(currentCollections.map((collection) => collection.apiName));

	for (const collection of store.$collections) {
		if (collection.meta?.fromWorkspaceCollections && !collectionNames.has(collection.name)) {
			removeCollection(store, collection.name);
		}
	}
}

export const useWorkspaceSchemaStore = defineStore('workspace-schema', () => {
	const { data: manifest, isRefreshing, refresh } = workspaceSchemaHydration.use();
	const querySchema = computed(() => manifest.value ? new QuerySchemaWalker(manifest.value) : null);
	const dataSources = computed(() => {
		const store = workspaceRuntime.activeRuntime.value?.store;
		return store && manifest.value ? manifestDataSources(store, manifest.value) : [];
	});

	const allCollections = computed(() => dataSources.value.flatMap((source) => source.collections));
	const allPrimitiveFields = computed(() => allCollections.value.flatMap((collection) => collection.primitiveFields));
	const allSingleRelationFields = computed(() => allCollections.value.flatMap((collection) => collection.relationFields));

	const collectionsById = computed(() => Object.fromEntries(allCollections.value.map((collection) => [collection.id, collection])));
	const primitiveFieldsById = computed(() => Object.fromEntries(allPrimitiveFields.value.map((field) => [field.id, field])));
	const singleRelationFieldsById = computed(() => Object.fromEntries(allSingleRelationFields.value.map((field) => [field.id, field])));

	return {
		dataSources,
		querySchema,
		collections: allCollections,
		nonSystemCollections: computed(() => allCollections.value.filter(({ isSystem }) => !isSystem)),
		primitiveFields: allPrimitiveFields,

		getCollection,
		findFieldById,
		findFieldByName,

		findSourceById,
		findCollectionById,
		findPrimitiveFieldById,
		findPrimitiveFieldByName,
		findCollectionByName,
		findSingleRelationFieldById,
		findSingleRelationFieldByName,

		isRefreshing,
		refresh,
	};

	function findSourceById(sourceId: GlobalDataSourceId): ResolvedMonospaceDataSource | undefined {
		return dataSources.value.find(({ id }) => id === sourceId);
	}

	function findCollectionById(collectionId: GlobalCollectionId): ResolvedMonospaceCollection | undefined {
		return collectionsById.value[collectionId];
	}

	function findCollectionByName(collectionName: string): ResolvedMonospaceCollection | undefined {
		return allCollections.value.find((collection) => collection.apiName === collectionName);
	}

	function findPrimitiveFieldById(fieldId: GlobalPrimitiveFieldId): ResolvedMonospacePrimitiveField | undefined {
		return primitiveFieldsById.value[fieldId];
	}

	function findPrimitiveFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string): ResolvedMonospacePrimitiveField | undefined {
		return toValue(allPrimitiveFields).find((field) => {
			return field.apiName === fieldName && field.collection?.id === toValue(collection).id;
		});
	}

	function findFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string) {
		const relationField = findSingleRelationFieldByName(collection, fieldName);
		const primitiveField = findPrimitiveFieldByName(collection, fieldName);
		return relationField ?? primitiveField;
	}

	function findSingleRelationFieldById(fieldId: GlobalSingleRelationFieldId): ResolvedMonospaceSingleRelationField | undefined {
		return singleRelationFieldsById.value[fieldId];
	}

	function findFieldById(fieldId: GlobalFieldId) {
		const primitiveField = findPrimitiveFieldById(fieldId as GlobalPrimitiveFieldId);
		const relationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);
		return relationField ?? primitiveField;
	}

	function findSingleRelationFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string, fields: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField[]> = allSingleRelationFields): ResolvedMonospaceSingleRelationField | undefined {
		return toValue(fields).find((field) => {
			return field.apiName === fieldName && field.collection?.id === toValue(collection).id;
		});
	}

	function getCollection(collectionOrId: GlobalCollectionId | ResolvedMonospaceCollection): ResolvedMonospaceCollection | undefined {
		return typeof collectionOrId === 'string' ? findCollectionById(collectionOrId) : collectionOrId;
	}
});
